import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import {
  CATEGORIES,
  categoryAccent,
  categoryLabel,
} from "../../content/newsletters/categories";

const TEXT = {
  ko: { all: "전체", label: "카테고리" },
  en: { all: "All", label: "Categories" },
};

export function categoryPath(lang, categoryId) {
  return categoryId
    ? `/${lang}/newsletter/category/${categoryId}`
    : `/${lang}/newsletter`;
}

/**
 * 목록 위의 카테고리 탭. 탭마다 URL 이 있어 그대로 공유할 수 있다.
 *
 * 글이 아직 없는 카테고리도 보여 준다 — 어떤 게임 소식이 이 지면에 실리는지 알 수 있게.
 * 다만 "0" 은 정보가 없으니 글 수는 글이 있을 때만 붙인다.
 * 화면 폭과 상관없이 한 줄로 두고, 넘치면 가로로 밀어 본다. 넘친 쪽 가장자리는 흐리게
 * 해서 더 있다는 걸 알리고, 고른 탭이 화면 밖에 있으면 안으로 끌어온다.
 * 게임 색은 고른 탭의 밑줄(과 hover 밑줄)로만 보여 준다.
 */
function CategoryNav({ lang, active, counts, total }) {
  const t = TEXT[lang] || TEXT.ko;
  const trackRef = useRef(null);
  const activeRef = useRef(null);
  const [overflow, setOverflow] = useState({ start: false, end: false });

  const updateOverflow = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    const max = track.scrollWidth - track.clientWidth;
    const next = { start: track.scrollLeft > 1, end: track.scrollLeft < max - 1 };
    setOverflow((prev) =>
      prev.start === next.start && prev.end === next.end ? prev : next
    );
  }, []);

  // 고른 탭이 칸 밖에 있으면 안으로 끌어온다. 창 폭이 바뀔 때도 다시 맞춘다.
  const revealActive = useCallback(() => {
    const track = trackRef.current;
    const tab = activeRef.current;
    if (track && tab) {
      const visible =
        tab.offsetLeft >= track.scrollLeft &&
        tab.offsetLeft + tab.offsetWidth <= track.scrollLeft + track.clientWidth;
      if (!visible) {
        // scrollIntoView 는 페이지 세로 스크롤까지 건드릴 수 있어 가로만 직접 맞춘다.
        const left = tab.offsetLeft - (track.clientWidth - tab.offsetWidth) / 2;
        track.scrollLeft = Math.max(0, left);
      }
    }
    updateOverflow();
  }, [updateOverflow]);

  useEffect(revealActive, [active, revealActive]);

  useEffect(() => {
    window.addEventListener("resize", revealActive);
    // 웹폰트가 늦게 들어오면 탭 폭이 바뀐다.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(revealActive);
    }
    return () => window.removeEventListener("resize", revealActive);
  }, [revealActive]);

  const items = [
    { id: null, label: t.all, count: total, accent: null },
    ...CATEGORIES.map((category) => ({
      id: category.id,
      label: categoryLabel(category, lang),
      count: counts[category.id] || 0,
      accent: categoryAccent(category),
    })),
  ];

  return (
    <Nav aria-label={t.label}>
      <Track
        ref={trackRef}
        onScroll={updateOverflow}
        $fadeStart={overflow.start}
        $fadeEnd={overflow.end}
      >
        {items.map((item) => {
          const isActive = item.id === active;
          return (
            <Tab
              key={item.id || "all"}
              ref={isActive ? activeRef : undefined}
              to={categoryPath(lang, item.id)}
              aria-current={isActive ? "page" : undefined}
              $active={isActive}
              style={item.accent ? { "--nl-tab-accent": item.accent } : undefined}
            >
              {item.label}
              {item.count > 0 ? <Count>{item.count}</Count> : null}
            </Tab>
          );
        })}
      </Track>
    </Nav>
  );
}

const FADE = "1.5rem";
// 포커스 테두리 자리로 탭 좌우에 둔 여백. 첫 글자가 제목과 줄이 맞게 칸을 그만큼 넓힌다.
const TAB_INSET = "0.125rem";

function fadeMask({ $fadeStart, $fadeEnd }) {
  if (!$fadeStart && !$fadeEnd) return "";
  const start = $fadeStart ? `transparent 0, #000 ${FADE}` : "#000 0";
  const end = $fadeEnd ? `#000 calc(100% - ${FADE}), transparent 100%` : "#000 100%";
  const mask = `linear-gradient(to right, ${start}, ${end})`;
  return `-webkit-mask-image: ${mask}; mask-image: ${mask};`;
}

const Nav = styled.nav`
  @media screen and (max-width: 600px) {
    /* 본문 칸 좌우 여백까지 밀어 쓸 수 있게 넓히고, 같은 만큼 안쪽 여백을 준다. */
    margin: 0 -1.5rem;
  }
`;

const Track = styled.div`
  position: relative;
  display: flex;
  gap: 0 0.875rem;
  margin: 0 -${TAB_INSET};
  overflow-x: auto;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
  ${fadeMask}

  &::-webkit-scrollbar {
    display: none;
  }

  @media screen and (max-width: 600px) {
    margin: 0;
    padding: 0 calc(1.5rem - ${TAB_INSET});
  }
`;

/** 글 목록의 카테고리 표시(PostLink)에서 쓰는 게임 색 점. */
export const Dot = styled.span`
  display: inline-block;
  flex: none;
  width: 0.4375rem;
  height: 0.4375rem;
  border-radius: 50%;
  background: var(--nl-tab-accent, var(--nl-accent));
`;

const Count = styled.span`
  font-size: 0.6875rem;
  font-weight: 500;
  color: var(--nl-ink-mute);
  font-variant-numeric: tabular-nums;
`;

/* 가로 스크롤 칸 안이라 바깥 테두리는 잘린다. 포커스 테두리는 탭 안쪽에 그린다. */
const Tab = styled(Link)`
  display: inline-flex;
  flex: none;
  align-items: baseline;
  gap: 0.3125rem;
  padding: 0.5rem ${TAB_INSET} 0.5625rem;
  border-bottom: 2px solid
    ${({ $active }) =>
      $active ? "var(--nl-tab-accent, var(--nl-ink))" : "transparent"};
  font-size: 0.875rem;
  font-weight: ${({ $active }) => ($active ? 700 : 500)};
  color: ${({ $active }) => ($active ? "var(--nl-ink)" : "var(--nl-ink-soft)")};
  white-space: nowrap;
  text-decoration: none;
  transition: color 0.18s ease, border-color 0.18s ease;

  &:hover {
    color: var(--nl-ink);
    ${({ $active }) =>
      $active
        ? ""
        : "border-bottom-color: color-mix(in srgb, var(--nl-tab-accent, var(--nl-ink)) 40%, transparent);"}
  }

  &:focus-visible {
    outline: 2px solid var(--nl-tab-accent, var(--nl-accent));
    outline-offset: -2px;
    border-radius: 3px;
  }
`;

export default CategoryNav;
